import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { interpolateTemplateBody, } from '@wit-support/admin-contracts';
import { composeOutbound, toInnerHtml } from '@wit-support/email-render';
import { Button, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, HtmlPreview, Input, Label, Textarea, toast, InlineNotice, } from '@wit-support/ui';
import { filterTemplatesByName, TemplatePicker } from '@wit-support/ui-domain';
import { ArrowLeft, Eye, FileText, Hourglass, Reply, Send, X } from 'lucide-react';
import { ConversationMergedError, conversationsApiBaseUrl, postReply, createScheduledEmail, ScheduledEmailRequestError, } from '../api.js';
import { ScheduleControl } from '../scheduled/schedule-control.js';
import { StatusAfterSendDialog } from './status-after-send-dialog.js';
import { fetchTemplates } from '../components/bulk-data.js';
async function fetchEmailLayout() {
    const r = await fetch(`${conversationsApiBaseUrl()}/api/email-layout`, {
        credentials: 'include',
    });
    if (!r.ok)
        return null;
    return (await r.json());
}
const withReplyPrefix = (s) => (/^re:\s/i.test(s) ? s : `Re: ${s}`);
/**
 * Warns that a pipeline is parked mid-dialogue with this CONTACT and that a
 * manual answer ends it. Rendered above the composer in BOTH its collapsed and
 * expanded states — the operator about to press «Ответить» is exactly who needs
 * to read it, and by the time the editor is open the decision is already made.
 *
 * Deliberately NOT a block: an operator always outranks the bot, so the send
 * button stays live. The banner states the consequence and lets them choose —
 * the promise it makes is real, `ReplySendPg` emits
 * `conversation.pipeline_operator_took_over` and the worker un-parks the run
 * down its escalation branch.
 *
 * Two wordings, because the takeover is contact-wide while the operator is
 * looking at one dialog. `conversationNumber` non-null means the park lives
 * somewhere ELSE in this person's dialogs: the text then names that dialog and
 * says «здесь тоже», so the operator is not told an unrelated reply is harmless.
 * Null means the park is right here, and the plain wording stays.
 */
function WaitingRunBanner({ waitingRun }) {
    if (!waitingRun)
        return null;
    const deadline = waitingRun.waitDeadline ? new Date(waitingRun.waitDeadline) : null;
    const until = deadline && !Number.isNaN(deadline.getTime())
        ? ` до ${deadline.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        })}`
        : '';
    const elsewhere = waitingRun.conversationNumber != null;
    return (_jsx(InlineNotice, { bordered: true, icon: Hourglass, role: "status", className: "mb-2", children: elsewhere ? (_jsxs("span", { children: [_jsxs("strong", { className: "font-medium", children: ["\u0423 \u044D\u0442\u043E\u0433\u043E \u043A\u043B\u0438\u0435\u043D\u0442\u0430 \u0431\u043E\u0442 \u0436\u0434\u0451\u0442 \u043E\u0442\u0432\u0435\u0442\u0430", until, " \u2014 \u00AB", waitingRun.pipelineName, "\u00BB (\u0434\u0438\u0430\u043B\u043E\u0433\u00A0\u2116\u00A0", waitingRun.conversationNumber, ")."] }), ' ', "\u0412\u0430\u0448 \u0440\u0443\u0447\u043D\u043E\u0439 \u043E\u0442\u0432\u0435\u0442 \u0437\u0434\u0435\u0441\u044C \u0442\u043E\u0436\u0435 \u0441\u043D\u0438\u043C\u0435\u0442 \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u043A\u0443: \u043F\u0440\u043E\u0433\u043E\u043D \u0443\u0439\u0434\u0451\u0442 \u043E\u043F\u0435\u0440\u0430\u0442\u043E\u0440\u0443, \u0438 \u0441\u043B\u0435\u0434\u0443\u044E\u0449\u0435\u0435 \u043F\u0438\u0441\u044C\u043C\u043E \u043A\u043B\u0438\u0435\u043D\u0442\u0430 \u0435\u0433\u043E \u0443\u0436\u0435 \u043D\u0435 \u0432\u043E\u0437\u043E\u0431\u043D\u043E\u0432\u0438\u0442."] })) : (_jsxs("span", { children: [_jsxs("strong", { className: "font-medium", children: ["\u0411\u043E\u0442 \u0436\u0434\u0451\u0442 \u043E\u0442\u0432\u0435\u0442\u0430 \u043A\u043B\u0438\u0435\u043D\u0442\u0430", until, " \u2014 \u00AB", waitingRun.pipelineName, "\u00BB."] }), ' ', "\u0412\u0430\u0448 \u0440\u0443\u0447\u043D\u043E\u0439 \u043E\u0442\u0432\u0435\u0442 \u0441\u043D\u0438\u043C\u0435\u0442 \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u043A\u0443: \u043F\u0440\u043E\u0433\u043E\u043D \u0443\u0439\u0434\u0451\u0442 \u043E\u043F\u0435\u0440\u0430\u0442\u043E\u0440\u0443, \u0438 \u0441\u043B\u0435\u0434\u0443\u044E\u0449\u0435\u0435 \u043F\u0438\u0441\u044C\u043C\u043E \u043A\u043B\u0438\u0435\u043D\u0442\u0430 \u0435\u0433\u043E \u0443\u0436\u0435 \u043D\u0435 \u0432\u043E\u0437\u043E\u0431\u043D\u043E\u0432\u0438\u0442."] })) }));
}
/**
 * Reply composer with two paths:
 *
 *   - **Text reply**: the agent types a plain message; the server auto-
 *     paragraphs it and wraps in the branded layout. No HTML editing.
 *   - **Template send**: the agent picks a pre-built template from the
 *     dropdown; a confirmation dialog shows the full rendered preview and
 *     a single Send button. The template body is never exposed for editing
 *     in the composer — changes go through `/templates`.
 *
 * The composer stays collapsed by default (just a "Reply to X" bar) and
 * auto-collapses after a successful send.
 */
export function ReplyComposer({ conversationId, conversation, currentAdminDisplayName, canReply, }) {
    const [expanded, setExpanded] = useState(false);
    // Within the expanded panel: 'templates' is the primary quick-pick mode
    // (search + grid of template buttons); 'manual' reveals the free-form editor
    // (subject + textarea + preview). Raising the panel always lands on templates.
    const [mode, setMode] = useState('templates');
    const [subject, setSubject] = useState('');
    const [text, setText] = useState('');
    const [search, setSearch] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [templateToSend, setTemplateToSend] = useState(null);
    // Opened after a successful manual/template send so the operator can pick a
    // status for the conversation (the email is already out; status is an
    // optional second step). Shown only by this single web composer.
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const didPrefillRef = useRef(false);
    const textareaRef = useRef(null);
    // Fresh idempotency key per Send attempt — retried POSTs (network blip,
    // React StrictMode double-invoke, double-click) carry the same key so the
    // server collapses them onto the existing message row. Resets on terminal
    // success or failure so the next user-initiated send carries a genuinely
    // new key. This prevents an edited resend from being silently collapsed as
    // a duplicate when the server committed the message row before the provider
    // send failed (commit-then-send ordering in reply-send-pg.ts).
    const idempotencyKeyRef = useRef(null);
    // Same pattern for the schedule path: a stable key per scheduling attempt so
    // an automatic retry of the SAME attempt collapses to one pending row
    // (server onConflict), while a fresh user-initiated schedule gets a new key.
    const scheduleIdempotencyKeyRef = useRef(null);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    useEffect(() => {
        if (didPrefillRef.current)
            return;
        didPrefillRef.current = true;
        const base = conversation.subject?.trim();
        if (base)
            setSubject(withReplyPrefix(base));
    }, [conversation.subject]);
    const templatesQuery = useQuery({
        queryKey: ['templates'],
        queryFn: fetchTemplates,
        enabled: canReply,
        staleTime: 60_000,
    });
    const layoutQuery = useQuery({
        queryKey: ['email-layout'],
        queryFn: fetchEmailLayout,
        enabled: canReply,
        staleTime: 5 * 60_000,
    });
    const filteredTemplates = useMemo(() => filterTemplatesByName(templatesQuery.data, search), [templatesQuery.data, search]);
    const freshIdempotencyKey = () => {
        if (!idempotencyKeyRef.current) {
            idempotencyKeyRef.current =
                typeof crypto !== 'undefined' && 'randomUUID' in crypto
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }
        return idempotencyKeyRef.current;
    };
    const freshScheduleIdempotencyKey = () => {
        if (!scheduleIdempotencyKeyRef.current) {
            scheduleIdempotencyKeyRef.current =
                typeof crypto !== 'undefined' && 'randomUUID' in crypto
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }
        return scheduleIdempotencyKeyRef.current;
    };
    const mutation = useMutation({
        mutationFn: (input) => postReply(conversationId, input, { idempotencyKey: freshIdempotencyKey() }),
        onSuccess: () => {
            setText('');
            setSubject('');
            setExpanded(false);
            setMode('templates');
            setTemplateToSend(null);
            didPrefillRef.current = false;
            idempotencyKeyRef.current = null;
            toast.success('Ответ отправлен');
            void queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
            void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
            void queryClient.invalidateQueries({ queryKey: ['conversations'] });
            // Additive: after the existing collapse/clear/invalidate, prompt for an
            // optional status change. The email is already sent — this is a second,
            // skippable step. Covers both the manual and the template send path
            // (both flow through this onSuccess).
            setStatusDialogOpen(true);
        },
        onError: (err) => {
            // Roll the idempotency key so the next user-initiated send is a genuine
            // new request and not collapsed as a duplicate. The server may have
            // committed the message row before the provider send failed
            // (commit-then-send in reply-send-pg.ts), so the old key is permanently
            // claimed. Without this reset an edited resend would silently return
            // the stale row (isDuplicate=true) and never dispatch the updated text.
            idempotencyKeyRef.current = null;
            if (err instanceof ConversationMergedError) {
                if (err.targetId) {
                    toast.success('Redirected — this conversation was merged');
                    void navigate({
                        to: '/conversations/$conversationId',
                        params: { conversationId: err.targetId },
                    });
                }
                else {
                    // Merge detected but the target id is unreadable (malformed 303 /
                    // cross-origin redirect) — can't navigate; prompt a refresh.
                    toast.error('Эта переписка была объединена — обновите страницу');
                }
                return;
            }
            toast.error(err.message);
        },
    });
    /** Schedule mutation — same content, but creates a scheduled_email row instead of sending immediately. */
    const scheduleMut = useMutation({
        mutationFn: (input) => createScheduledEmail(conversationId, {
            ...(input.templateId
                ? {
                    templateId: input.templateId,
                }
                : { bodyHtml: input.bodyHtml ?? '' }),
            // Template branch must not carry a subject (DB CHECK + contract refine).
            subject: input.templateId ? null : (input.subject ?? null),
            scheduleFor: input.scheduleFor,
        }, { idempotencyKey: freshScheduleIdempotencyKey() }),
        onSuccess: () => {
            setText('');
            setSubject('');
            setExpanded(false);
            setMode('templates');
            setTemplateToSend(null);
            didPrefillRef.current = false;
            scheduleIdempotencyKeyRef.current = null;
            toast.success('Письмо запланировано');
            void queryClient.invalidateQueries({ queryKey: ['scheduled-emails', conversationId] });
            // Refresh the inbox list so a newly-scheduled conversation appears under
            // the «С отложенными письмами» (hasScheduledEmail) filter immediately
            // rather than waiting up to 10s for the next poll.
            void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        },
        onError: (err) => {
            if (err instanceof ConversationMergedError) {
                // The source conversation was tombstoned + merged — navigate to the
                // surviving target so the operator re-schedules there (mirrors the
                // immediate-reply `mutation.onError` merge handling above). Clearing the
                // key here is correct REGARDLESS of whether the target is known: a merge
                // is a definitive resolution, not an ambiguous 5xx, so the next attempt
                // should be a fresh request, not a key replay onto the old conversation.
                scheduleIdempotencyKeyRef.current = null;
                if (err.targetId) {
                    toast.success('Redirected — this conversation was merged');
                    void navigate({
                        to: '/conversations/$conversationId',
                        params: { conversationId: err.targetId },
                    });
                }
                else {
                    toast.error('Эта переписка была объединена — обновите страницу');
                }
                return;
            }
            // Roll the idempotency key ONLY on a DEFINITIVE server rejection (4xx — the
            // row was NOT created), so the next attempt is a genuine new request. On an
            // AMBIGUOUS failure (network error or 5xx — the server MAY have committed
            // the scheduled_email before the response was lost), PRESERVE the key so a
            // user retry COLLAPSES onto that committed row via the server's idempotency
            // dedup instead of creating a SECOND schedule → two customer emails. Unlike
            // the immediate-reply path (which rolls on any failure because a failed send
            // leaves a re-sendable 'failed' row), a scheduled row that was created WILL
            // send, so a duplicate must be collapsed, not re-created.
            const status = err instanceof ScheduledEmailRequestError ? err.status : undefined;
            const definitiveRejection = status !== undefined && status >= 400 && status < 500;
            if (definitiveRejection) {
                scheduleIdempotencyKeyRef.current = null;
            }
            toast.error(err.message);
        },
    });
    function handleScheduleText(scheduleFor) {
        if (!text.trim())
            return;
        // Produce the SAME inner HTML the immediate Send path yields: the server
        // composes a `bodyText` reply through `composeOutbound` → `toInnerHtml`
        // (blank-line→`<p>`, single `\n`→`<br/>`, HTML-escape). The scheduled path
        // only carries `bodyHtml`, so we run that exact helper here client-side and
        // the worker's later `replySend.reply({ bodyHtml })` (which re-runs
        // `toInnerHtml`, a no-op once tags are present) reproduces an identical email.
        scheduleMut.mutate({
            scheduleFor,
            bodyHtml: toInnerHtml(text),
            // The custom (text) branch REQUIRES a non-null subject server-side (it
            // mirrors the DB `scheduled_email_content_ck`). Subject-less conversations
            // (e.g. web-form) leave the field blank, so fall back to the same derived
            // subject the immediate-send preview uses (`effectiveSubject`): the
            // conversation subject with a `Re:` prefix, or a bare `Re:` when there's
            // none. Without this the schedule POST 422s on subject-less dialogs.
            subject: subject.trim() || withReplyPrefix(conversation.subject ?? '').trim() || 'Re:',
        });
    }
    function handleScheduleTemplate(scheduleFor) {
        if (!templateToSend)
            return;
        scheduleMut.mutate({
            scheduleFor,
            templateId: templateToSend.id,
            subject: subject.trim() || null,
        });
    }
    if (!canReply)
        return null;
    const recipientLabel = conversation.contact?.displayName?.trim() ||
        conversation.contact?.primaryEmail?.trim() ||
        'customer';
    const recipientEmail = conversation.contact?.primaryEmail ?? null;
    // Collapsed state — thin bar with a single Reply CTA.
    if (!expanded) {
        return (_jsxs("div", { className: "sticky bottom-0 z-10 border-t border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]", children: [_jsxs("div", { className: "mx-auto w-full max-w-[740px] px-4", children: [_jsx(WaitingRunBanner, { waitingRun: conversation.waitingRun }), _jsxs(Button, { type: "button", variant: "secondary", className: "w-full", onClick: () => {
                                setMode('templates');
                                setSearch('');
                                setExpanded(true);
                            }, children: [_jsx(Reply, { className: "h-4 w-4", "aria-hidden": "true" }), "\u041E\u0442\u0432\u0435\u0442\u0438\u0442\u044C \u2014 ", recipientLabel] })] }), _jsx(StatusAfterSendDialog, { open: statusDialogOpen, conversationId: conversationId, onClose: () => setStatusDialogOpen(false) })] }));
    }
    const canSendText = text.trim().length > 0 && !mutation.isPending && !scheduleMut.isPending;
    const handleSendText = () => {
        if (!canSendText)
            return;
        mutation.mutate({
            subject: subject.trim() || null,
            bodyText: text,
        });
    };
    const handleKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            handleSendText();
        }
    };
    const collapse = () => {
        if (mutation.isPending)
            return;
        setExpanded(false);
    };
    // Preview composes the branded shell against the current subject/text so
    // agents see exactly what the recipient will receive. For the template
    // confirm dialog, `templateToSend.body` replaces `text` as the body source.
    const renderComposedPreview = (bodyText, bodySubject) => {
        const layout = layoutQuery.data;
        if (!layout) {
            // Fallback while layout still loading — just show the raw text.
            return `<p>${bodyText}</p>`;
        }
        return composeOutbound({
            layout: { html: layout.html },
            agent: { displayName: currentAdminDisplayName, signatureHtml: null },
            contact: {
                displayName: conversation.contact?.displayName ?? null,
                email: conversation.contact?.primaryEmail ?? null,
            },
            subject: bodySubject,
            bodyHtml: bodyText,
        }).html;
    };
    const templateContext = {
        contact: {
            name: conversation.contact?.displayName ?? null,
            email: conversation.contact?.primaryEmail ?? null,
        },
        agent: { name: currentAdminDisplayName },
        // `number` обязателен: превью должно совпадать с тем, что подставит сервер
        // в `ReplySendPg.reply()` — расхождение уже ловили на `{{contact.name}}`.
        conversation: {
            id: conversation.id,
            number: conversation.number,
            subject: conversation.subject,
        },
    };
    const effectiveSubject = subject || withReplyPrefix(conversation.subject ?? '');
    const plainTextPreviewHtml = text.trim() ? renderComposedPreview(text, effectiveSubject) : '';
    // Тема подтверждения отправки шаблона. ТОЛЬКО ветка `templateToSend.subject`
    // проходит через подстановку — ровно её сервер и интерполирует в
    // `ReplySendPg.reply()` (`templateSubject`, без HTML-escape: это plain-text
    // заголовок). Ветка `effectiveSubject` — это `conversation.subject_base` с
    // префиксом «Re:», сервер её НЕ интерполирует, поэтому и мы не трогаем.
    // Без этого китовое письмо (номер обращения стоит в ТЕМЕ) показывало бы
    // оператору сырой `{{conversation.number}}` там, где уйдёт живой номер.
    const templateConfirmSubject = templateToSend
        ? templateToSend.subject
            ? interpolateTemplateBody(templateToSend.subject, templateContext)
            : effectiveSubject
        : '';
    return (_jsxs("div", { className: "sticky bottom-0 z-10 border-t border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]", onKeyDown: handleKeyDown, children: [_jsxs("div", { className: "mx-auto w-full max-w-[740px] px-4", children: [_jsx(WaitingRunBanner, { waitingRun: conversation.waitingRun }), _jsxs("div", { className: "mb-2 flex items-center justify-between gap-2", children: [_jsx("div", { className: "flex min-w-0 items-center gap-2", children: mode === 'manual' ? (_jsxs(Button, { type: "button", variant: "ghost", size: "sm", onClick: () => setMode('templates'), disabled: mutation.isPending, children: [_jsx(ArrowLeft, { className: "h-4 w-4", "aria-hidden": "true" }), "\u041A \u0448\u0430\u0431\u043B\u043E\u043D\u0430\u043C"] })) : (_jsxs("span", { className: "truncate text-sm font-medium text-[var(--color-text-primary)]", children: ["\u041E\u0442\u0432\u0435\u0442 \u2014 ", recipientLabel] })) }), _jsx(Button, { type: "button", variant: "ghost", size: "sm", "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u0440\u0435\u0434\u0430\u043A\u0442\u043E\u0440", onClick: collapse, disabled: mutation.isPending, children: _jsx(X, { className: "h-4 w-4", "aria-hidden": "true" }) })] }), mode === 'templates' ? (
                    // PRIMARY mode: quick template pick. Search + a grid of template
                    // buttons; clicking one opens the confirm dialog (preview + send/
                    // schedule). A «Написать вручную» entry switches to the free editor.
                    _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(TemplatePicker, { templates: filteredTemplates, isLoading: templatesQuery.isLoading, search: search, onSearchChange: setSearch, onPick: (t) => {
                                    setSearch('');
                                    setTemplateToSend(t);
                                }, listContainerClassName: "max-h-56 overflow-auto", listClassName: "grid grid-cols-1 gap-1.5 sm:grid-cols-2", nameClassName: "w-full truncate text-sm font-medium text-[var(--color-text-primary)]", emptyMessage: templatesQuery.data && templatesQuery.data.length > 0
                                    ? 'Шаблоны не найдены.'
                                    : 'Шаблонов пока нет — напишите ответ вручную.' }), _jsx("div", { className: "mt-1 flex items-center justify-between gap-2 border-t border-[var(--color-border-subtle)] pt-2", children: _jsxs(Button, { type: "button", variant: "ghost", size: "sm", onClick: () => {
                                        setMode('manual');
                                        queueMicrotask(() => textareaRef.current?.focus());
                                    }, disabled: mutation.isPending, children: [_jsx(FileText, { className: "h-4 w-4", "aria-hidden": "true" }), "\u041D\u0430\u043F\u0438\u0441\u0430\u0442\u044C \u0432\u0440\u0443\u0447\u043D\u0443\u044E"] }) })] })) : (
                    // FREE-FORM mode: subject + textarea + preview, with the same
                    // send/schedule controls as before.
                    _jsxs("div", { className: "flex flex-col", children: [_jsxs("div", { className: "mb-2", children: [_jsx(Label, { htmlFor: "reply-subject", className: "sr-only", children: "\u0422\u0435\u043C\u0430" }), _jsx(Input, { id: "reply-subject", value: subject, onChange: (e) => setSubject(e.target.value), placeholder: `Re: ${conversation.subject ?? ''}`.trim(), "aria-label": "\u0422\u0435\u043C\u0430", disabled: mutation.isPending })] }), _jsx(Textarea, { ref: textareaRef, value: text, onChange: (e) => setText(e.target.value), placeholder: `Введите ответ для ${recipientLabel}…`, rows: 4, "aria-label": "\u0422\u0435\u043A\u0441\u0442 \u043E\u0442\u0432\u0435\u0442\u0430", "data-testid": "reply-body", disabled: mutation.isPending, className: "min-h-[6rem] resize-y" }), _jsxs("div", { className: "mt-2 flex flex-wrap items-center justify-between gap-2", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsxs(Button, { type: "button", variant: "ghost", size: "sm", "aria-label": "\u041F\u0440\u0435\u0434\u043F\u0440\u043E\u0441\u043C\u043E\u0442\u0440 \u043E\u0442\u0432\u0435\u0442\u0430", onClick: () => setPreviewOpen(true), disabled: !text.trim(), children: [_jsx(Eye, { className: "h-4 w-4", "aria-hidden": "true" }), "\u041F\u0440\u0435\u0434\u043F\u0440\u043E\u0441\u043C\u043E\u0442\u0440"] }), _jsx("span", { className: "hidden text-xs text-[var(--color-text-muted)] md:inline", children: "Cmd+Enter \u2014 \u043E\u0442\u043F\u0440\u0430\u0432\u0438\u0442\u044C" })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(ScheduleControl, { onSchedule: handleScheduleText, disabled: !text.trim() || mutation.isPending || scheduleMut.isPending }), _jsxs(Button, { type: "button", onClick: handleSendText, disabled: !canSendText, "aria-label": "\u041E\u0442\u043F\u0440\u0430\u0432\u0438\u0442\u044C \u043E\u0442\u0432\u0435\u0442", children: [_jsx(Send, { className: "h-4 w-4", "aria-hidden": "true" }), mutation.isPending ? 'Отправка…' : 'Отправить'] })] })] })] }))] }), _jsx(Dialog, { open: previewOpen, onOpenChange: setPreviewOpen, children: _jsxs(DialogContent, { size: "page", children: [_jsxs(DialogHeader, { className: "gap-4", children: [_jsx(DialogTitle, { children: "\u041F\u0440\u0435\u0434\u043F\u0440\u043E\u0441\u043C\u043E\u0442\u0440" }), _jsxs(DialogDescription, { children: ["\u0422\u0430\u043A ", recipientLabel, " \u0443\u0432\u0438\u0434\u0438\u0442 \u044D\u0442\u043E\u0442 \u043E\u0442\u0432\u0435\u0442."] }), _jsxs("div", { className: "space-y-1 text-sm", children: [_jsxs("div", { children: [_jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u041A\u043E\u043C\u0443: " }), _jsx("span", { children: recipientEmail ?? recipientLabel })] }), _jsxs("div", { children: [_jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u0422\u0435\u043C\u0430: " }), _jsx("span", { children: effectiveSubject })] })] })] }), _jsx(DialogBody, { children: _jsx(HtmlPreview, { html: plainTextPreviewHtml, height: "100%" }) })] }) }), templateToSend ? (_jsx(Dialog, { open: templateToSend !== null, onOpenChange: (v) => {
                    if (!v)
                        setTemplateToSend(null);
                }, children: _jsxs(DialogContent, { size: "page", children: [_jsxs(DialogHeader, { className: "gap-4", children: [_jsxs(DialogTitle, { children: ["\u041E\u0442\u043F\u0440\u0430\u0432\u0438\u0442\u044C \u00AB", templateToSend.name, "\u00BB"] }), _jsxs(DialogDescription, { children: ["\u041F\u043E\u043B\u043D\u043E\u0435 \u043E\u0444\u043E\u0440\u043C\u043B\u0435\u043D\u043D\u043E\u0435 \u043F\u0438\u0441\u044C\u043C\u043E \u0431\u0443\u0434\u0435\u0442 \u043E\u0442\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u043E ", recipientLabel, ". \u0427\u0442\u043E\u0431\u044B \u0438\u0437\u043C\u0435\u043D\u0438\u0442\u044C \u0442\u0435\u043A\u0441\u0442, \u043E\u0442\u0440\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u0443\u0439\u0442\u0435 \u0448\u0430\u0431\u043B\u043E\u043D \u0432 \u0440\u0430\u0437\u0434\u0435\u043B\u0435 \u00AB\u0428\u0430\u0431\u043B\u043E\u043D\u044B\u00BB."] }), _jsxs("div", { className: "space-y-1 text-sm", children: [_jsxs("div", { children: [_jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u041A\u043E\u043C\u0443: " }), _jsx("span", { children: recipientEmail ?? recipientLabel })] }), _jsxs("div", { children: [_jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u0422\u0435\u043C\u0430: " }), _jsx("span", { children: templateConfirmSubject })] })] })] }), _jsx(DialogBody, { children: _jsx(HtmlPreview, { html: renderComposedPreview(interpolateTemplateBody(templateToSend.body, templateContext, {
                                    escapeHtml: true,
                                }), templateConfirmSubject), height: "100%" }) }), _jsxs(DialogFooter, { className: "flex-wrap items-center", children: [_jsx(Button, { type: "button", variant: "ghost", onClick: () => setTemplateToSend(null), disabled: mutation.isPending || scheduleMut.isPending, children: "\u041E\u0442\u043C\u0435\u043D\u0430" }), _jsx(ScheduleControl, { onSchedule: handleScheduleTemplate, disabled: mutation.isPending || scheduleMut.isPending }), _jsxs(Button, { type: "button", onClick: () => {
                                        mutation.mutate({
                                            templateId: templateToSend.id,
                                            subject: subject.trim() || null,
                                        });
                                    }, disabled: mutation.isPending || scheduleMut.isPending, children: [_jsx(Send, { className: "h-4 w-4", "aria-hidden": "true" }), mutation.isPending ? 'Отправка…' : 'Отправить шаблон'] })] })] }) })) : null, _jsx(StatusAfterSendDialog, { open: statusDialogOpen, conversationId: conversationId, onClose: () => setStatusDialogOpen(false) })] }));
}
//# sourceMappingURL=reply-composer.js.map